/** Shapes returned by the /api functions. Mirrors api/src/shared/normalize.js. */

export type CheckState = 'success' | 'failure' | 'error' | 'pending' | null
export type EnvKey = 'dev' | 'stage' | 'production'

export interface Person {
  alias: string
  name: string
  avatar: string | null
}

export interface Commit {
  sha: string
  message: string
  author: Person
  committedAt: string
  checks: CheckState
}

export interface BranchPr {
  number: number
  url: string
  base: string
  draft: boolean
  reviewDecision: string | null
}

export interface Branch {
  name: string
  env: EnvKey | null
  role?: string
  protected: boolean
  isDefault: boolean
  checks: CheckState
  totalCommits: number
  pr: BranchPr | null
  commits: Commit[]
}

export interface PullRequest {
  number: number
  title: string
  url: string
  draft: boolean
  base: string
  head: string
  createdAt: string
  updatedAt: string
  author: Person
  reviewDecision: 'APPROVED' | 'CHANGES_REQUESTED' | 'REVIEW_REQUIRED' | null
  approvals: number
  changesRequested: number
  mergeable: 'MERGEABLE' | 'CONFLICTING' | 'UNKNOWN'
  checks: CheckState
  additions: number
  deletions: number
  changedFiles: number
}

export interface Promotion {
  from: EnvKey
  to: EnvKey
  sourceBranch: string
  targetBranch: string
  /** Commits on the source the target does not have: what is waiting to promote. */
  ahead: number | null
  /** Commits on the target the source does not have. Null when not comparable. */
  behind: number | null
  status: 'AHEAD' | 'BEHIND' | 'DIVERGED' | 'IDENTICAL' | 'UNKNOWN'
}

export interface RepoState {
  repo: string
  owner: string
  url: string
  description: string
  defaultBranch: string
  private: boolean
  archived: boolean
  pushedAt: string
  viewerPermission: 'ADMIN' | 'MAINTAIN' | 'WRITE' | 'TRIAGE' | 'READ'
  sample: false
  sourceLabel: string
  fetchedAt: string
  envMapping: Partial<Record<EnvKey, string>>
  /** Where each environment is actually deployed, from .github/branch-viz.json. Only https survives. */
  environmentUrls: Partial<Record<EnvKey, string>>
  promotions: Promotion[]
  branchCount: number
  truncatedBranches: boolean
  branches: Branch[]
  prCount: number
  pullRequests: PullRequest[]
  rate: { remaining: number; resetAt: string; cost: number } | null
  syncMs: number
  syncedBy: string
  hasOverride: boolean
}

export interface RepoSummary {
  name: string
  fullName: string
  description: string
  private: boolean
  archived: boolean
  pushedAt: string
  permission: string
  defaultBranch: string | null
  language: string | null
}

export interface Me {
  configured: boolean
  org: string
  authenticated: boolean
  user: { login: string; name: string; avatar: string } | null
}

export interface SupabaseProjectSummary {
  id: string
  name: string
  region: string | null
  status: string
  statusLabel: string
  healthy: boolean
  createdAt: string | null
  host: string | null
}

export interface AzureServerSummary {
  id: string
  name: string
  /** From the resource's own `repo` tag: which repository this database belongs to. */
  repo: string | null
  env: string | null
  role: string | null
  resourceGroup: string | null
  subscriptionId: string | null
  location: string | null
  host: string | null
  state: string
  statusLabel: string
  healthy: boolean
  engine: string
  version: string | null
  tier: string | null
  sku: string | null
  storageGb: number | null
  backupRetentionDays: number | null
  geoRedundantBackup: boolean
  highAvailability: boolean
  publicNetworkAccess: boolean | null
  entraAuth: boolean
  createdAt: string | null
  portalUrl: string
}

export interface AzureTableMetrics {
  /** Entities across the whole account. Azure reports no per-table breakdown. */
  entities: number | null
  capacityBytes: number | null
  /** Table operations in the last 24 hours: whether anything is still writing. */
  transactions24h: number | null
  sampledAt: string | null
}

export interface AzureTableStoreSummary {
  id: string
  name: string
  repo: string | null
  env: string | null
  /** From the resource's `purpose` tag. */
  purpose: string | null
  /** From the resource's `data` tag, e.g. "personal data". */
  dataClass: string | null
  resourceGroup: string | null
  subscriptionId: string | null
  location: string | null
  host: string | null
  state: string
  statusLabel: string
  healthy: boolean
  engine: string
  sku: string | null
  /** Table names only. Reader never sees a row. */
  tables: string[]
  metrics: AzureTableMetrics | null
  minimumTlsVersion: string | null
  publicNetworkAccess: boolean | null
  createdAt: string | null
  portalUrl: string
}

export interface AzureSqlDatabaseSummary {
  id: string
  name: string
  server: string | null
  repo: string | null
  env: string | null
  role: string | null
  resourceGroup: string | null
  subscriptionId: string | null
  location: string | null
  host: string | null
  state: string
  statusLabel: string
  healthy: boolean
  engine: string
  version: string | null
  tier: string | null
  sku: string | null
  maxSizeGb: number | null
  autoPauseMinutes: number | null
  publicNetworkAccess: boolean | null
  entraOnlyAuth: boolean
  createdAt: string | null
  portalUrl: string
}

export interface DatabasesResponse {
  supabase: {
    configured: boolean
    projects: SupabaseProjectSummary[]
    error?: string
  }
  azure: {
    configured: boolean
    servers: AzureServerSummary[]
    tableStores: AzureTableStoreSummary[]
    sqlDatabases: AzureSqlDatabaseSummary[]
    error?: string
  }
}

export type ProbeStatus = 'up' | 'protected' | 'error' | 'down' | 'unreachable' | 'blocked'
export type ProbeErrorKind = 'http' | 'dns' | 'tls' | 'timeout' | 'refused' | 'reset' | 'unreachable' | 'network' | 'blocked' | null

export interface EnvironmentHealth {
  env: EnvKey
  url: string
  status: ProbeStatus
  httpStatus: number | null
  /** Round trip in milliseconds, null when the request never completed. */
  ms: number | null
  error: string | null
  errorKind: ProbeErrorKind
  /** Set only when redirects moved the request somewhere else. */
  finalUrl: string | null
  redirects: number
  /** Up to 600 characters of a failing response's own text. Null when healthy. */
  body: string | null
}

export interface RepoHealth {
  scope: 'repo'
  org: string
  repo: string
  checkedAt: string
  tookMs: number
  declared: number
  environments: EnvironmentHealth[]
}

export interface OrgHealthRepo {
  repo: string
  declared: number
  environments: EnvironmentHealth[]
  error?: string
}

export interface OrgHealth {
  scope: 'org'
  org: string
  checkedAt: string
  tookMs: number
  scanned: number
  truncated: boolean
  repos: OrgHealthRepo[]
}

export class ApiError extends Error {
  status: number
  unauthenticated: boolean
  constructor(status: number, message: string, unauthenticated = false) {
    super(message)
    this.status = status
    this.unauthenticated = unauthenticated
  }
}
